import { animate, state, style, transition, trigger } from '@angular/animations';
import { Location } from '@angular/common';
import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { MainComponent } from 'src/app/layout/main/main.component';

@Component({
	selector: '[app-submenu]',
	templateUrl: 'sub-menu.component.html',
	animations: [
		trigger('children', [
			state(
				'void',
				style({
					height: '0px',
				})
			),
			state(
				'hiddenAnimated',
				style({
					height: '0px',
				})
			),
			state(
				'visibleAnimated',
				style({
					height: '*',
				})
			),
			state(
				'visible',
				style({
					height: '*',
					'z-index': 100,
				})
			),
			state(
				'hidden',
				style({
					height: '0px',
					'z-index': '*',
				})
			),
			transition('visibleAnimated => hiddenAnimated', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
			transition('hiddenAnimated => visibleAnimated', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
			transition('void => visibleAnimated, visibleAnimated => void', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
		]),
	],
	standalone: false,
})
export class SubMenuComponent {
	@Input() item: MenuItem | MenuItem[];
	@Input() root: boolean;
	@Input() visible: boolean;
	@Input() collapsed = false;
	_reset: boolean;
	_parentActive: boolean;
	activeIndex: number;

	constructor(
		public app: MainComponent,
		public router: Router,
		public location: Location
	) {}

	get menuItems(): MenuItem[] {
		return this.root ? (this.item as MenuItem[]) : (this.item as MenuItem).items || [];
	}

	itemClick(event: Event, item: MenuItem, index: number) {
		// Handle external links
		if (item.url && item.target === '_blank') {
			window.open(item.url, '_blank');
			event.preventDefault();
			return;
		}

		// Handle regular external links without target
		if (item.url && !item.routerLink) {
			window.location.href = item.url;
			event.preventDefault();
			return;
		}

		if (this.root) {
			this.app.menuHoverActive = !this.app.menuHoverActive;
			event.preventDefault();
		}

		// avoid processing disabled items
		if (item.disabled) {
			event.preventDefault();
			return true;
		}

		// activate current item and deactivate active sibling if any
		if (item.items) {
			this.activeIndex = (this.activeIndex as number) === index ? -1 : index;
		} else {
			this.activeIndex = -1;
		}

		// execute command
		if (item.command) {
			item.command({ originalEvent: event, item: item });
		}

		// hide menu
		if (!item.items) {
			if (this.app.isMobile()) {
				this.app.mobileMenuActive = false;
			}

			this.app.resetMenu = false;
			this.app.menuHoverActive = !this.app.menuHoverActive;
		}
	}

	getColorKey(item: MenuItem): string | null {
		const key = (item as MenuItem & { colorKey?: string }).colorKey;
		return key?.toLowerCase() ?? null;
	}

	isLinkActive(item: MenuItem): boolean {
		const link = this.normalizedRouterLink(item);
		if (!link) return false;

		return this.isRouteActive(link);
	}

	isActive(item: MenuItem, index: number): boolean {
		return this.isMenuLinkFocused(index) || this.isChildMenuLinkActive(index) || this.isLinkActive(item);
	}

	private normalizedRouterLink(item: MenuItem): string | null {
		const link = item.routerLink as string | string[] | undefined;

		if (!link) return null;

		if (Array.isArray(link)) {
			const fullPath = link
				.filter(segment => segment !== null && segment !== undefined && `${segment}`.trim().length > 0)
				.map(segment => `${segment}`.replace(/^\/+|\/+$/g, ''))
				.join('/');

			return fullPath ? `/${fullPath}` : null;
		}

		return `${link}`;
	}

	private isRouteActive(link: string): boolean {
		const currentUrl = this.stripUrlDecorators(this.router.url);
		const normalizedLink = this.stripUrlDecorators(link);

		if (!normalizedLink) return false;

		return currentUrl === normalizedLink || currentUrl.startsWith(`${normalizedLink}/`);
	}

	private stripUrlDecorators(url: string): string {
		const withoutQuery = (url ?? '').split('?')[0].split('#')[0];
		const normalized = withoutQuery.replace(/\/+$/, '');
		return normalized || '/';
	}

	isMenuLinkFocused(index: number): boolean {
		return this.activeIndex === index;
	}

	isChildMenuLinkActive(index: number): boolean {
		let isChildActive = false;
		const itemArray = this.menuItems;
		if (!itemArray || !itemArray[index]) return false;

		const tempItem = { ...itemArray[index] };
		const sameURLValue = tempItem?.items?.filter(data => {
			const childLink = this.normalizedRouterLink(data);
			return childLink ? this.isRouteActive(childLink) : false;
		});

		if (!tempItem?.routerLink) {
			isChildActive = sameURLValue?.length ? sameURLValue.length > 0 : false;
		}
		return isChildActive;
	}

	@Input() get reset(): boolean {
		return this._reset;
	}

	set reset(val: boolean) {
		this._reset = val;
		if (this._reset && this.app.isOverlay()) {
			this.activeIndex = null;
		}
	}

	@Input() get parentActive(): boolean {
		return this._parentActive;
	}

	set parentActive(val: boolean) {
		this._parentActive = val;
		if (!this._parentActive) {
			this.activeIndex = null;
		}
	}

	get dialogWidth(): number {
		return window.outerWidth * 0.8;
	}
}
